import { useState, useCallback, useEffect } from "react"
import { lumi } from "../lib/lumi"

// 멘션/참조 대상으로 선택 가능한 사용자
export interface SelectableUser {
  userId: string
  userName: string
  email: string
  teamName?: string
}

/**
 * 멘션/참조자 지정에 사용할 등록 사용자 목록을 조회한다.
 * userProfiles3 에서 이메일/이름이 존재하고 승인된 사용자만 후보로 노출한다.
 */
export function useSelectableUsers() {
  const [users, setUsers] = useState<SelectableUser[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const { list } = await lumi.entities.userProfiles3.list({
          projection: { userId: 1, userName: 1, email: 1, teamName: 1, role: 1, userRole: 1, approvalStatus: 1, enabled: 1 },
        })
        const mapped: SelectableUser[] = (list || [])
          // 후보 노출 조건:
          // - 이메일 보유
          // - 승인 완료(approvalStatus === "approved")
          // - 비활성화되지 않음(enabled !== false)
          // - 경영지원 마스터(관리자) 제외: 경영지원 팀 소속이면서 관리자 역할인 계정은 제외
          .filter((u: any) => {
            if (!u?.email) return false
            if (u?.approvalStatus !== "approved") return false
            if (u?.enabled === false) return false
            const team = (u?.teamName || "").trim()
            const role = String(u?.role || u?.userRole || "").toLowerCase()
            const isAdminRole = role === "admin" || role === "administrator"
            if (team === "경영지원" && isAdminRole) return false
            return true
          })
          .map((u: any) => ({
            userId: u.userId,
            userName: u.userName || u.email?.split("@")[0] || "사용자",
            email: u.email,
            teamName: u.teamName || "",
          }))
        if (!cancelled) {
          setUsers(mapped)
          setLoaded(true)
        }
        console.log("selectable users loaded:", mapped.length)
      } catch (error) {
        console.error("사용자 목록 조회 실패:", error)
        if (!cancelled) setLoaded(true)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  return { users, loaded }
}

interface NotifyTarget {
  email: string
  userName?: string
}

/**
 * 장비 이슈 관련 이메일 알림 발송 유틸.
 * - lumi.tools.email.send 는 등록 사용자에게만 발송 가능(미등록 이메일은 400)
 * - 알림 실패가 본 기능(이슈/코멘트 저장)을 막지 않도록 항상 try-catch 로 격리한다.
 */
export function useEquipmentNotify() {
  const [sending, setSending] = useState(false)

  const sendNotifications = useCallback(
    async (
      targets: NotifyTarget[],
      subject: string,
      html: string,
      senderEmail?: string
    ) => {
      // 중복 제거 + sender 본인 제외 + 빈 이메일 제외
      const uniqueEmails = Array.from(
        new Set(
          targets
            .map((t) => (t.email || "").trim().toLowerCase())
            .filter((e) => e && e !== (senderEmail || "").trim().toLowerCase())
        )
      )
      if (uniqueEmails.length === 0) return

      setSending(true)
      try {
        await lumi.tools.email.send({
          to: uniqueEmails,
          subject,
          fromName: "페이브텍 관리 시스템",
          html,
        })
        console.log("equipment notify sent:", uniqueEmails.length)
      } catch (error) {
        // 알림 실패는 사용자 작업을 막지 않음 (로그만 남김)
        console.error("이메일 알림 발송 실패:", error)
      } finally {
        setSending(false)
      }
    },
    []
  )

  return { sendNotifications, sending }
}

// 이메일 본문 템플릿 헬퍼
export function buildIssueEmail(params: {
  title: string
  equipmentName: string
  issueContent: string
  actorName: string
  extraLine?: string
  // extraLine 표시용 라벨 (기본: "내용") - 코멘트 알림에서는 "코멘트"로 사용
  extraLabel?: string
  // 이슈 분류(태그) - 존재하면 "분류" 행으로 표시
  classification?: string
  // 이슈보기 딥링크 URL - 존재하면 "이슈 보기" 버튼 표시
  linkUrl?: string
}) {
  const { title, equipmentName, issueContent, actorName, extraLine, extraLabel, classification, linkUrl } = params
  const extraTitle = extraLabel || "내용"
  return `
    <div style="font-family:Apple SD Gothic Neo,Malgun Gothic,sans-serif;max-width:560px;margin:0 auto;border:1px solid #f0f0f0;border-radius:12px;overflow:hidden">
      <div style="background:linear-gradient(135deg,#F59E0B,#D97706);padding:18px 24px">
        <h1 style="color:#fff;font-size:16px;margin:0">페이브텍 관리 시스템</h1>
      </div>
      <div style="padding:24px">
        <h2 style="font-size:15px;color:#111;margin:0 0 12px">${title}</h2>
        <table style="width:100%;font-size:13px;color:#444;border-collapse:collapse">
          <tr><td style="padding:6px 0;color:#999;width:90px">장비</td><td style="padding:6px 0">${equipmentName}</td></tr>
          ${classification ? `<tr><td style="padding:6px 0;color:#999">분류</td><td style="padding:6px 0;white-space:pre-line">${classification}</td></tr>` : ""}
          <tr><td style="padding:6px 0;color:#999">이슈 제목</td><td style="padding:6px 0;white-space:pre-line">${issueContent}</td></tr>
          ${extraLine ? `<tr><td style="padding:6px 0;color:#999">${extraTitle}</td><td style="padding:6px 0;white-space:pre-line">${extraLine}</td></tr>` : ""}
          <tr><td style="padding:6px 0;color:#999">처리자</td><td style="padding:6px 0">${actorName}</td></tr>
        </table>
        ${linkUrl ? `<div style="margin:22px 0 4px"><a href="${linkUrl}" style="display:inline-block;background:linear-gradient(135deg,#F59E0B,#D97706);color:#fff;text-decoration:none;font-size:13px;font-weight:600;padding:11px 22px;border-radius:8px">이슈 보기</a></div>` : ""}
        <p style="font-size:12px;color:#999;margin:20px 0 0">본 메일은 페이브텍 장비 이슈 관리 시스템에서 자동 발송되었습니다.</p>
      </div>
    </div>
  `
}
